import React, { useState, useRef } from "react";
import { api, mediaUrl, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { GifPicker } from "./GifPicker";
import { toast } from "sonner";

export function WallComposer({ wallOwnerId, ownerName, isOwner, onPosted }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]); // [{url, type}]
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef();

  const pickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((prev) => [...prev, { url: data.url, type: f.type.startsWith("video") ? "video" : "image" }]);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!text.trim() && media.length === 0) { toast.error("Write something or add a photo/video."); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/posts", { type: "wall", text, media, wall_owner_id: wallOwnerId });
      setText(""); setMedia([]);
      onPosted?.(data);
      toast.success(data.wall_status === "pending" ? "Sent for approval" : "Posted to the Scratch Post!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  return (
    <div className="bg-white border-4 border-black rounded-2xl p-4 brand-shadow mb-4" data-testid="wall-composer">
      <div className="flex gap-3">
        <Avatar user={user} size="md" />
        <div className="flex-1 min-w-0 space-y-3">
          <Textarea data-testid="wall-composer-text" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={isOwner ? "Write something on your own Scratch Post…" : `Write something on ${ownerName}'s Scratch Post…`}
            className="border-2 border-black resize-none min-h-[64px] text-base focus-visible:ring-spotter-gold" />
          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="wall-composer-previews">
              {media.map((m, i) => (
                <div key={i} className="relative aspect-square rounded-lg border-2 border-black overflow-hidden bg-black">
                  {m.type === "video"
                    ? <video src={mediaUrl(m.url)} className="w-full h-full object-cover" />
                    : <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                  <button data-testid={`wall-remove-media-${i}`} onClick={() => setMedia(media.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-black text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={pickMedia} />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" data-testid="wall-add-media" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="border-2 border-black gap-2 bg-white hover:bg-spotter-cream">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-spotter-gold" />} Photos / Videos
              </Button>
              <GifPicker onSelect={(url) => setMedia((prev) => [...prev, { url, type: "image" }])}
                trigger={<Button type="button" variant="outline" data-testid="wall-add-gif" className="border-2 border-black gap-2 bg-white hover:bg-spotter-cream font-heading font-bold text-spotter-maroon">GIF</Button>} />
            </div>
            <Button data-testid="wall-composer-submit" onClick={submit} disabled={posting || uploading}
              className="bg-spotter-maroon text-spotter-cream font-heading font-bold border-2 border-black brand-shadow-sm">
              {posting ? "Posting…" : "Post to Scratch Post"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
